import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Accordion module
 */
export default class Accordion extends MessageBus {
  /**
   * Constructor for the Accordion class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.init();
  }

  /**
   * Initializes the view by caching DOM references,
   * setting up event handlers and enabling accordion functionality.
   * @returns {Accordion} A reference to the current instance
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References.
   *
   * Finds and stores all necessary DOM elements used in the view.
   * @returns {Accordion} A reference to the current instance
   * @chainable
   */
  cacheDomReferences() {
    this.accordionWrapper = this.element;
    this.accordionItemsJs = this.element.querySelectorAll(
      `.${CLASS_NAMES.ACCORDION_ITEM_JS}`
    );
    this.accordionItems = this.element.querySelectorAll(
      `.${CLASS_NAMES.ACCORDION_ITEM}`
    );
    return this;
  }

  /**
   * Bind event handlers with the proper context if needed.
   * @returns {Accordion} A reference to the current instance
   * @chainable
   */
  setupHandlers() {
    return this;
  }

  /**
   * Enable the accordion behavior by binding the necessary event listeners.
   * @returns {Accordion} A reference to the current instance
   * @chainable
   */
  enable() {
    if (!this.accordionWrapper) {
      return this;
    }

    this.accordionItemsJs.forEach((item) => {
      item.addEventListener(EVENTS.CLICK, () => {
        const parent = item.parentElement;
        if (!parent) return;

        const container = parent.querySelector(
          `.${CLASS_NAMES.ACCORDION_CONTAINER}`
        );
        if (!container) return;

        const isOpen = window.getComputedStyle(container).display !== 'none';

        // 1️⃣ Close all OTHER open items
        // this.accordionItemsJs.forEach((otherItem) => {
        //   if (otherItem === item) return;

        //   const otherParent = otherItem.parentElement;
        //   if (!otherParent) return;

        //   const otherContainer = otherParent.querySelector(
        //     `.${CLASS_NAMES.ACCORDION_CONTAINER}`
        //   );
        //   if (!otherContainer) return;

        //   const otherIsOpen =
        //     window.getComputedStyle(otherContainer).display !== 'none';

        //   if (otherIsOpen) {
        //     Accordion.slideToggle(otherContainer, 300);
        //   }

        //   otherItem.classList.remove(CLASS_NAMES.ACCORDION_HIDE);

        //   const otherIcon = otherItem.querySelector(
        //     `.${CLASS_NAMES.ACCORDION_ICON}`
        //   );
        //   if (otherIcon) {
        //     otherIcon.classList.remove(CLASS_NAMES.ACCORDION_MINUS);
        //   }
        // });

        // 2️⃣ Toggle the clicked item
        Accordion.slideToggle(container, 300);

        if (isOpen) {
          item.classList.remove(CLASS_NAMES.ACCORDION_HIDE);
          const icon = item.querySelector(`.${CLASS_NAMES.ACCORDION_ICON}`);
          if (icon) icon.classList.remove(CLASS_NAMES.ACCORDION_MINUS);
        } else {
          item.classList.add(CLASS_NAMES.ACCORDION_HIDE);
          const icon = item.querySelector(`.${CLASS_NAMES.ACCORDION_ICON}`);
          if (icon) icon.classList.add(CLASS_NAMES.ACCORDION_MINUS);
        }
      });
    });

    this.accordionItems.forEach((item) => {
      item.addEventListener(EVENTS.KEY_DOWN, (e) => {
        if (
          e.target.matches('input, label, select, textarea, button, a') ||
          e.target.closest('.facetwp-facet')
        ) {
          return;
        }

        if (e.keyCode === 13 || e.keyCode === 32) {
          e.preventDefault();
          const itemJs = item.querySelector(
            `.${CLASS_NAMES.ACCORDION_ITEM_JS}`
          );
          if (itemJs) {
            itemJs.click();
          }
        }
      });
    });

    return this;
  }

  /**
   * A helper function to smoothly animate the slide toggle of an element.
   * Uses requestAnimationFrame for smooth animations.
   * @param {HTMLElement} element - The element to slide toggle.
   * @param {number} duration - Duration of the animation in milliseconds.
   */
  /**
   * Force a repaint on an element — fixes iOS Safari clip-path invalidation bug.
   * @param {HTMLElement} el - The element to force a repaint on.
   */
  static forceRepaint(el) {
    if (!el) return;
    const target = el;
    target.style.webkitTransform = 'translateZ(0) scale(1)';
    // eslint-disable-next-line no-unused-expressions
    target.offsetHeight; // trigger reflow
    target.style.webkitTransform = '';
  }

  static slideToggle(element, duration = 300) {
    if (!element) return;

    const computedStyle = window.getComputedStyle(element);
    const isHidden = computedStyle.display === 'none';
    const targetElement = element;
    const paddingBottom = 16;
    const clipPathParent = element.closest('.faq-accordion__content');

    if (isHidden) {
      // Slide Down
      targetElement.style.display = 'block';
      const targetHeight = targetElement.scrollHeight;
      targetElement.style.height = '0px';
      targetElement.style.paddingBottom = '0px';
      targetElement.style.overflow = 'hidden';

      let startTime = null;
      const animateDown = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        targetElement.style.height = `${targetHeight * progress}px`;
        targetElement.style.paddingBottom = `${paddingBottom * progress}px`;

        if (progress < 1) {
          requestAnimationFrame(animateDown);
        } else {
          targetElement.style.removeProperty('height');
          targetElement.style.removeProperty('overflow');
          targetElement.style.removeProperty('padding-bottom');
          Accordion.forceRepaint(clipPathParent);
        }
      };
      requestAnimationFrame(animateDown);
    } else {
      // Slide Up
      const startHeight = targetElement.scrollHeight + paddingBottom;
      targetElement.style.height = `${startHeight}px`;
      targetElement.style.paddingBottom = `${paddingBottom}px`;
      targetElement.style.overflow = 'hidden';

      let startTime = null;
      const animateUp = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        targetElement.style.height = `${startHeight * (1 - progress)}px`;
        targetElement.style.paddingBottom = `${
          paddingBottom * (1 - progress)
        }px`;

        if (progress < 1) {
          requestAnimationFrame(animateUp);
        } else {
          targetElement.style.display = 'none';
          targetElement.style.removeProperty('height');
          targetElement.style.removeProperty('overflow');
          targetElement.style.removeProperty('padding-bottom');
          Accordion.forceRepaint(clipPathParent);
        }
      };
      requestAnimationFrame(animateUp);
    }
  }
}
